// Debug logging utility
// To enable logging: window.DEBUG_LOGS = true
// To disable logging: window.DEBUG_LOGS = false
const debugLog = (...args: any[]) => {
    if (typeof window !== 'undefined' && (window as any).DEBUG_LOGS) {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
};

const debugError = (...args: any[]) => {
    if (typeof window !== 'undefined' && (window as any).DEBUG_LOGS) {
        // eslint-disable-next-line no-console
        console.error(...args);
    }
};

// Dynamic URL configuration based on environment
const isLocalhost = (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
);
const isDevelopment = (
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1')
);

export const API_BASE_URL = isDevelopment
    ? 'http://localhost:8000/api'
    : 'https://wadokaikarateka.kumitepro.com/api';

export const FILE_SERVER_URL = isDevelopment
    ? 'http://localhost:8000'
    : 'https://wadokaikarateka.kumitepro.com';

// Performance optimization: API Caching
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const pendingRequests = new Map();

// Cache management utilities
const getCacheKey = (endpoint: string, params?: any) => {
    // Include memberId in cache key if present
    const memberId = params?.memberId || params?.member_id;
    const cacheKey = memberId
        ? `${endpoint}_member_${memberId}_${JSON.stringify(params || {})}`
        : `${endpoint}_${JSON.stringify(params || {})}`;
    return cacheKey;
};

const isCacheValid = (cached: any) => {
    return cached && Date.now() - cached.timestamp < CACHE_DURATION;
};

const clearExpiredCache = () => {
    const now = Date.now();
    cache.forEach((value, key) => {
        if (now - value.timestamp > CACHE_DURATION) {
            cache.delete(key);
        }
    });
};

// Clear expired cache every 10 minutes
setInterval(clearExpiredCache, 10 * 60 * 1000);

// Cached fetch utility
const cachedFetch = async (endpoint: string, options: RequestInit = {}, params?: any) => {
    const cacheKey = getCacheKey(endpoint, params);
    debugLog('🔍 Cache lookup for key:', cacheKey);

    // Check cache first
    const cached = cache.get(cacheKey);
    if (isCacheValid(cached)) {
        debugLog('✅ Cache HIT for:', cacheKey);
        return cached.data;
    }

    debugLog('❌ Cache MISS for:', cacheKey);

    // Check if request is already pending
    if (pendingRequests.has(cacheKey)) {
        debugLog('⏳ Request already pending for:', cacheKey);
        return pendingRequests.get(cacheKey);
    }

    debugLog('📡 Making API request for:', endpoint);

    // Make the request
    const requestPromise = fetch(endpoint, options).then(async (response) => {
        const data = await response.json();

        // Cache successful responses
        if (response.ok) {
            debugLog('💾 Caching response for:', cacheKey);
            cache.set(cacheKey, {
                data,
                timestamp: Date.now()
            });
        }

        pendingRequests.delete(cacheKey);
        return data;
    }).catch((error) => {
        debugError('💥 Request failed for:', cacheKey, error);
        pendingRequests.delete(cacheKey);
        throw error;
    });

    pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
};

export interface ValidationResponse {
    success: boolean;
    message: string;
}

export interface LoginResponse {
    success: boolean;
    user: {
        id: number;
        name: string;
        mobile: string;
        created_at: string;
        updated_at: string;
    };
    token: string;
}

export interface Member {
    memberId: number;
    fullName: string;
    avatar: string;
    current_rank_title: string;
}

export interface MembersResponse {
    success: boolean;
    members: Member[];
}

export interface AttendanceRecord {
    attendanceId: number;
    attendanceDate: string; // e.g. '04-07-2025'
    ATTENDENCE: string; // e.g. '2025-07-04'
}

export interface AttendanceResponse {
    success: boolean;
    attendance: AttendanceRecord[];
    from_month: string;
}

export interface MemberProfile {
    membershipStatus: string;
    memberId: number;
    fullName: string;
    grNumber: string;
    contactNumber: string;
    dateOfBirth: string;
    emailId: string;
    bloodGroup: string;
    isActive: number;
    rank_title: string;
    avatar: string;
    nextRankId: number;
    nextRank: string;
    rank_id: number;
    regNumber: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    father_occupation: string;
    father_occupation_address: string;
    father_occupation_emailid: string;
    mother_occupation: string | null;
    mother_occupation_address: string | null;
    mother_occupation_emailid: string | null;
}

export interface MemberProfileResponse {
    success: boolean;
    member: MemberProfile;
}

export interface DojoDetails {
    coachName: string;
    coachEmail: string;
    coachContact: string;
    dojoName: string | null;
    dojoAddress: string;
    dojoRemark: string;
    schoolName: string | null;
    schoolContactName: string | null;
    schoolContactNumber: string | null;
    dojoClassDays: string;
    dojoFromTime: string;
    dojoToTime: string;
    dojoDuration: number;
}

export interface DojoResponse {
    success: boolean;
    data: DojoDetails;
}

export interface RankRecord {
    rank_name: string;
    certificateNumber: string;
    issueDate: string;
    rankId: number;
}

export interface RankResponse {
    success: boolean;
    nextRankId: number;
    nextRank: string;
    pending: number;
    ranks: RankRecord[];
}

export interface CompetitionRecord {
    comp_name: string;
    comp_date: string;
    team_kumite_res: string;
    kata_res: string;
    kumite_res: string;
    team_kata_res: string;
    team_kumite: number;
    kata: number;
    kumite: number;
    team_kata: number;
}

export interface CompetitionResponse {
    success: boolean;
    competitions: CompetitionRecord[];
}

export interface FeesRecord {
    id: number;
    fromDate: string;
    toDate: string;
    entry_date: string;
}

export interface FeesResponse {
    success: boolean;
    fees: FeesRecord[];
}

export interface ApplyExamResponse {
    success: boolean;
    message: string;
    examId?: number;
    examDate?: string;
    examLocation?: string;
}

export interface Announcement {
    id: number;
    title: string;
    content: string;
    type: 'info' | 'warning' | 'success' | 'urgent';
    priority: 'low' | 'medium' | 'high' | 'critical';
    publishDate: string;
    expiryDate?: string;
    isActive: boolean;
    author: string;
    targetAudience?: 'all' | 'students' | 'parents' | 'specific_rank';
    isRead?: boolean;
}

export interface AnnouncementsResponse {
    success: boolean;
    announcements: Announcement[];
}

export const apiService = {
    // Verify mobile number with backend
    async verifyMobile(mobile: string): Promise<ValidationResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ mobile }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            return await response.json();
        } catch (error) {
            debugError('Error verifying mobile:', error);
            throw error;
        }
    },

    // Login with mobile number
    async loginWithMobile(mobile: string): Promise<LoginResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/login_me`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ mobile }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            return await response.json();
        } catch (error) {
            debugError('Error logging in:', error);
            throw error;
        }
    },

    // Get members/students list
    async getMembers(): Promise<MembersResponse> {
        try {
            const token = this.getToken();
            if (!token) {
                throw new Error('No authentication token found');
            }

            const cacheKey = getCacheKey('members');
            const cached = cache.get(cacheKey);
            if (isCacheValid(cached)) {
                return cached.data;
            }

            const response = await fetch(`${API_BASE_URL}/members`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            // Cache the response
            cache.set(cacheKey, {
                data,
                timestamp: Date.now()
            });

            return data;
        } catch (error) {
            debugError('Error fetching members:', error);
            throw error;
        }
    },

    // Store token in localStorage
    storeToken(token: string): void {
        localStorage.setItem('authToken', token);
    },

    // Get token from localStorage
    getToken(): string | null {
        return localStorage.getItem('authToken');
    },

    // Remove token from localStorage
    removeToken(): void {
        localStorage.removeItem('authToken');
    },

    // Check if user is authenticated
    isAuthenticated(): boolean {
        return !!this.getToken();
    },

    // Upload avatar for a member
    async uploadAvatar(memberId: number, avatarFile: File): Promise<ValidationResponse> {
        try {
            const token = this.getToken();
            if (!token) {
                throw new Error('No authentication token found');
            }

            const formData = new FormData();
            formData.append('memberId', memberId.toString());
            formData.append('avatar', avatarFile);

            const response = await fetch(`${API_BASE_URL}/upload_avatar`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            return await response.json();
        } catch (error) {
            debugError('Error uploading avatar:', error);
            throw error;
        }
    },

    async getAttendance(memberId: number): Promise<AttendanceResponse> {
        const token = this.getToken();
        if (!token) throw new Error('No authentication token found');
        const endpoint = `${API_BASE_URL}/attendance/${memberId}`;
        return cachedFetch(endpoint, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }, { memberId });
    },

    async getMemberProfile(memberId: number): Promise<MemberProfileResponse> {
        const token = this.getToken();
        if (!token) throw new Error('No authentication token found');

        const cacheKey = getCacheKey('member_profile', { memberId });
        const cached = cache.get(cacheKey);
        if (isCacheValid(cached)) {
            return cached.data;
        }

        const response = await fetch(`${API_BASE_URL}/member/${memberId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        if (!response.ok) throw new Error('Failed to fetch member profile');

        const data = await response.json();

        // Cache the response
        cache.set(cacheKey, {
            data,
            timestamp: Date.now()
        });

        return data;
    },

    async getDojoDetails(memberId: number): Promise<DojoResponse> {
        const token = this.getToken();
        if (!token) throw new Error('No authentication token found');
        const endpoint = `${API_BASE_URL}/dojo/${memberId}`;
        return cachedFetch(endpoint, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }, { memberId });
    },

    async getRankDetails(memberId: number): Promise<RankResponse> {
        const token = this.getToken();
        if (!token) throw new Error('No authentication token found');
        const endpoint = `${API_BASE_URL}/ranks/${memberId}`;
        return cachedFetch(endpoint, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }, { memberId });
    },

    async getCompetitionDetails(memberId: number): Promise<CompetitionResponse> {
        const token = this.getToken();
        if (!token) throw new Error('No authentication token found');
        const endpoint = `${API_BASE_URL}/competition/${memberId}`;
        return cachedFetch(endpoint, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }, { memberId });
    },

    async getFeesDetails(memberId: number): Promise<FeesResponse> {
        const token = this.getToken();
        if (!token) throw new Error('No authentication token found');
        const endpoint = `${API_BASE_URL}/fees/${memberId}`;
        return cachedFetch(endpoint, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }, { memberId });
    },

    async applyForExam(memberId: number, examRankId: number): Promise<ApplyExamResponse> {
        debugLog('🚀 applyForExam called with:', { memberId, examRankId });

        try {
            const token = this.getToken();
            if (!token) {
                debugError('❌ No authentication token found');
                throw new Error('No authentication token found');
            }

            debugLog('📡 Making API call to:', `${API_BASE_URL}/apply_for_exam/${memberId}`);

            const response = await fetch(`${API_BASE_URL}/apply_for_exam/${memberId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ exam_rank_id: examRankId }),
            });

            debugLog('📥 Response status:', response.status);
            debugLog('📥 Response ok:', response.ok);

            if (!response.ok) {
                debugError('❌ HTTP error! status:', response.status);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: ApplyExamResponse = await response.json();
            debugLog('📥 Response data:', data);
            return data;
        } catch (error) {
            debugError('💥 Apply for exam API call failed:', error);
            throw error;
        }
    },

    async getAnnouncements(memberId: number): Promise<AnnouncementsResponse> {
        const token = this.getToken();
        if (!token) throw new Error('No authentication token found');
        const response = await fetch(`${API_BASE_URL}/announcements/${memberId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        if (!response.ok) throw new Error('Failed to fetch announcements');
        return response.json();
    },

    // Update mailing details
    async updateMailingDetail(memberId: number, data: {
        address: string;
        city: string;
        state: string;
        pincode: string;
    }): Promise<ValidationResponse> {
        try {
            const token = this.getToken();
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_BASE_URL}/update_mailing_detail/${memberId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();

            // Clear related cache on successful update
            if (result.success) {
                this.clearMemberCache(memberId);
            }

            return result;
        } catch (error) {
            debugError('Error updating mailing details:', error);
            throw error;
        }
    },

    // Update father occupation details
    async updateFatherOccupationDetail(memberId: number, data: {
        father_occupation: string;
        father_occupation_address: string;
        father_occupation_emailid?: string;
    }): Promise<ValidationResponse> {
        try {
            const token = this.getToken();
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_BASE_URL}/update_father_occupation_detail/${memberId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();

            // Clear related cache on successful update
            if (result.success) {
                this.clearMemberCache(memberId);
            }

            return result;
        } catch (error) {
            debugError('Error updating father occupation details:', error);
            throw error;
        }
    },

    // Update mother occupation details
    async updateMotherOccupationDetail(memberId: number, data: {
        mother_occupation?: string;
        mother_occupation_address?: string;
        mother_occupation_emailid?: string;
    }): Promise<ValidationResponse> {
        try {
            const token = this.getToken();
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${API_BASE_URL}/update_mother_occupation_detail/${memberId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            return await response.json();
        } catch (error) {
            debugError('Error updating mother occupation details:', error);
            throw error;
        }
    },

    // Clear cache for a specific member
    clearMemberCache(memberId: number) {
        debugLog('🧹 Clearing cache for member:', memberId);
        const cacheKeys = Array.from(cache.keys());
        debugLog('📊 Total cache keys before clearing:', cacheKeys.length);
        debugLog('🔍 All cache keys:', cacheKeys);

        let clearedCount = 0;
        cacheKeys.forEach(key => {
            // Check for various patterns that might match rank data
            const isRankData = key.includes(`ranks_${memberId}`) ||
                key.includes(`/ranks/${memberId}`) ||
                key.includes(`_member_${memberId}`) ||
                key.includes(`member_profile_${memberId}`) ||
                key.includes(`attendance_${memberId}`) ||
                key.includes(`dojo_${memberId}`) ||
                key.includes(`competition_${memberId}`) ||
                key.includes(`fees_${memberId}`);

            if (isRankData) {
                cache.delete(key);
                clearedCount++;
                debugLog('🗑️ Cleared cache key:', key);
            }
        });

        debugLog('✅ Cleared', clearedCount, 'cache entries for member', memberId);
        debugLog('📊 Remaining cache keys:', Array.from(cache.keys()).length);

        // Also clear any pending requests for this member
        const pendingKeys = Array.from(pendingRequests.keys());
        pendingKeys.forEach(key => {
            if (key.includes(`_member_${memberId}`) || key.includes(`/ranks/${memberId}`)) {
                pendingRequests.delete(key);
                debugLog('🗑️ Cleared pending request:', key);
            }
        });
    },

    // Get cache status for debugging
    getCacheStatus() {
        return {
            size: cache.size,
            keys: Array.from(cache.keys()),
            entries: Array.from(cache.entries()).map(([key, value]) => ({
                key,
                timestamp: value.timestamp,
                age: Date.now() - value.timestamp
            }))
        };
    },

    // Parallel API calls for better performance
    async getMemberDataParallel(memberId: number) {
        const token = this.getToken();
        if (!token) throw new Error('No authentication token found');

        try {
            const [profile, attendance, ranks, competitions, fees] = await Promise.all([
                this.getMemberProfile(memberId),
                this.getAttendance(memberId),
                this.getRankDetails(memberId),
                this.getCompetitionDetails(memberId),
                this.getFeesDetails(memberId)
            ]);

            return {
                profile,
                attendance,
                ranks,
                competitions,
                fees
            };
        } catch (error) {
            debugError('Error fetching member data in parallel:', error);
            throw error;
        }
    },
}; 